import { useRef, useState } from "react";
import { Film, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { errMsg, img } from "@/lib/community";

const CHUNK = 5 * 1024 * 1024;

export function VideoUpload({ value, onChange, disabled = false }) {
  const [progress, setProgress] = useState(null);
  const ref = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 150 * 1024 * 1024) return toast.error("Video is too large (max 150MB)");
    const type = file.type || (file.name.toLowerCase().endsWith(".mov") ? "video/quicktime" : "video/mp4");
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    const uploadId = crypto.randomUUID();
    setProgress(0);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", String(i));
        fd.append("total", String(total));
        fd.append("filename", file.name);
        fd.append("content_type", type);
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
        const { data } = await api.post("/admin/community/video/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setProgress(Math.round(((i + 1) / total) * 100));
        if (data.done) {
          onChange(data.url);
          toast.success("Video uploaded — remember to save changes");
        }
      }
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setProgress(null);
      if (ref.current) ref.current.value = "";
    }
  };

  const uploaded = value && value.startsWith("/api/community/media/");
  return (
    <div className="space-y-3" data-testid="founder-video-upload">
      {uploaded && (
        <div className="relative">
          <video src={img(value)} controls playsInline className="w-full max-h-72 rounded bg-black" data-testid="founder-video-preview" />
          <button type="button" onClick={() => onChange("")} className="absolute top-2 right-2 bg-white/90 rounded-full p-2" data-testid="founder-video-remove-btn" aria-label="Remove video">
            <Trash2 className="w-4 h-4 text-rose-600" />
          </button>
        </div>
      )}
      <button type="button" disabled={disabled || progress !== null} onClick={() => ref.current?.click()}
        className="w-full border border-dashed border-[#C9A97A] rounded-md py-5 text-sm text-[#7A6A62] flex items-center justify-center gap-2 hover:bg-[#FBF8F3] disabled:opacity-70"
        data-testid="founder-video-upload-btn">
        {progress !== null ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading… {progress}%</> : <><Film className="w-4 h-4" /> {uploaded ? "Replace video" : "Upload video from your phone or computer"}</>}
      </button>
      <input ref={ref} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v,.mov,.mp4" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="founder-video-file-input" />
      <p className="text-[11px] text-[#7A6A62]">MP4, MOV or WEBM up to 150MB. Tip: on iPhone, set Camera › Formats to “Most Compatible” so it plays in every browser.</p>
    </div>
  );
}
